/**
 * assets/senze-image-zoom.js
 * 2026-08-24 新增
 *
 * PDP 主图悬停放大镜：鼠标悬停即触发（不需要先点一下），用 CSS transform
 * （scale + transform-origin）做连续跟随缩放，compositor 层操作，不触发重绘。
 *
 * 用事件委托（监听 document 上的 pointermove/pointerleave），而不是给每张图
 * 单独绑事件：
 * 1. Dawn 切换 variant 时（product-info.js 的 updateMedia）会对画廊里的 <li>
 *    做增删和重新排序，如果用普通 addEventListener 挂在具体图片节点上，
 *    节点被移动/替换后事件可能跟着丢，委托到 document 就没有这个问题。
 * 2. 只需要一份监听器，不用管当前画廊里到底渲染了几张图。
 *
 * 只在支持真实 hover 的设备上生效（配合 CSS 的 (hover: hover) and
 * (pointer: fine)媒体查询判断一致）；触屏设备完全不会跑这段逻辑。
 */
(function () {
  'use strict';

  if (!window.matchMedia || !window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
    return;
  }

  var activeWrap = null;

  function getZoomScale(wrap) {
    var raw = getComputedStyle(wrap).getPropertyValue('--senze-zoom-scale');
    var parsed = parseFloat(raw);
    return isNaN(parsed) ? 2.2 : parsed;
  }

  function resetWrap(wrap) {
    var img = wrap.querySelector('.senze-zoom-img');
    if (img) {
      img.style.transform = '';
      img.style.transformOrigin = '';
    }
  }

  function applyZoom(wrap, clientX, clientY) {
    var img = wrap.querySelector('.senze-zoom-img');
    if (!img) return;

    var rect = wrap.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) return;

    var xPercent = ((clientX - rect.left) / rect.width) * 100;
    var yPercent = ((clientY - rect.top) / rect.height) * 100;
    xPercent = Math.max(0, Math.min(100, xPercent));
    yPercent = Math.max(0, Math.min(100, yPercent));

    img.style.transformOrigin = xPercent + '% ' + yPercent + '%';
    img.style.transform = 'scale(' + getZoomScale(wrap) + ')';
  }

  document.addEventListener('pointermove', function (event) {
    // 只响应鼠标；触屏的 pointerType 是 'touch'，不需要处理
    if (event.pointerType && event.pointerType !== 'mouse') return;

    var wrap = event.target.closest ? event.target.closest('.senze-zoom-wrap') : null;

    if (wrap !== activeWrap) {
      if (activeWrap) resetWrap(activeWrap);
      activeWrap = wrap;
    }

    if (wrap) applyZoom(wrap, event.clientX, event.clientY);
  });

  // 鼠标整个移出浏览器窗口时的兜底重置（正常在页面内移动时，
  // 上面 pointermove 里 wrap !== activeWrap 的判断已经会处理离开）
  document.addEventListener('pointerleave', function () {
    if (activeWrap) {
      resetWrap(activeWrap);
      activeWrap = null;
    }
  });
})();

/**
 * 2026-08-24 新增：点击放大弹窗（全屏 + 双指缩放 / 双击缩放）。
 * 跟上面悬停放大镜是完全独立的一套逻辑，两者互不依赖：
 * - 悬停放大镜只在支持 hover 的设备上生效（鼠标）
 * - 这一套点击/触摸都能触发，是移动端唯一的放大方式
 *
 * 弹窗在整个 PDP 只有一份（不跟着画廊图片重复渲染），点击任意一张
 * 主图时复用同一个弹窗、换成对应图片的高清版本。
 */
(function () {
  'use strict';

  var modal = document.querySelector('.senze-zoom-modal');
  if (!modal) return;

  var modalImg = modal.querySelector('.senze-zoom-modal__img');
  var closeBtn = modal.querySelector('.senze-zoom-modal__close');
  var viewport = modal.querySelector('.senze-zoom-modal__viewport');

  var scale = 1;
  var translateX = 0;
  var translateY = 0;
  var MAX_SCALE = 4;
  var DOUBLE_TAP_SCALE = 2.5;

  function applyTransform() {
    modalImg.style.transform = 'translate(' + translateX + 'px, ' + translateY + 'px) scale(' + scale + ')';
  }

  function resetTransform() {
    scale = 1;
    translateX = 0;
    translateY = 0;
    applyTransform();
  }

  // 缩放倍数越大，允许拖动平移的范围也越大，避免图片被拖出视野后找不回来
  function clampTranslate() {
    var rect = viewport.getBoundingClientRect();
    var maxX = (rect.width * (scale - 1)) / 2;
    var maxY = (rect.height * (scale - 1)) / 2;
    translateX = Math.max(-maxX, Math.min(maxX, translateX));
    translateY = Math.max(-maxY, Math.min(maxY, translateY));
  }

  // Shopify CDN 图片 URL 用 ?width= 参数控制尺寸；缩略图/主图之前请求的
  // 宽度不够弹窗放大看细节，这里换成一个更大的宽度重新请求（Shopify 会
  // 按需缩放原图，只要原图分辨率够，不会拉伸糊）
  function getHighResUrl(imgEl) {
    var src = imgEl.currentSrc || imgEl.src;
    if (!src) return src;
    if (/[?&]width=\d+/.test(src)) {
      return src.replace(/([?&])width=\d+/, '$1width=2048');
    }
    return src;
  }

  function openModal(triggerImg) {
    modalImg.src = getHighResUrl(triggerImg);
    modalImg.alt = triggerImg.alt || '';
    resetTransform();
    modal.classList.add('is-open');
    modal.setAttribute('aria-hidden', 'false');
    document.documentElement.classList.add('senze-zoom-modal-open');
  }

  function closeModal() {
    modal.classList.remove('is-open');
    modal.setAttribute('aria-hidden', 'true');
    document.documentElement.classList.remove('senze-zoom-modal-open');
    resetTransform();
  }

  // 点击/点触发大图：事件委托到 document，原因跟上面悬停放大镜一样——
  // 画廊图片会随 variant 切换增删/重排，委托能避免节点换掉后事件跟着丢。
  document.addEventListener('click', function (event) {
    var trigger = event.target.closest ? event.target.closest('.senze-zoom-trigger') : null;
    if (!trigger) return;
    var img = trigger.tagName === 'IMG' ? trigger : trigger.querySelector('img');
    if (!img) return;
    openModal(img);
  });

  closeBtn.addEventListener('click', closeModal);
  modal.addEventListener('click', function (event) {
    if (event.target === modal) closeModal();
  });
  document.addEventListener('keydown', function (event) {
    if (event.key === 'Escape' && modal.classList.contains('is-open')) closeModal();
  });

  // ---- 双指缩放 + 拖动平移（触屏） ----
  var pinchStartDistance = 0;
  var pinchStartScale = 1;
  var isPanning = false;
  var panStartX = 0;
  var panStartY = 0;
  var panOriginX = 0;
  var panOriginY = 0;
  var lastTapTime = 0;

  function touchDistance(touches) {
    var dx = touches[0].clientX - touches[1].clientX;
    var dy = touches[0].clientY - touches[1].clientY;
    return Math.sqrt(dx * dx + dy * dy);
  }

  function toggleZoom() {
    modalImg.classList.remove('is-panning');
    if (scale > 1) {
      resetTransform();
    } else {
      scale = DOUBLE_TAP_SCALE;
      translateX = 0;
      translateY = 0;
      applyTransform();
    }
  }

  modalImg.addEventListener('touchstart', function (event) {
    if (event.touches.length === 2) {
      pinchStartDistance = touchDistance(event.touches);
      pinchStartScale = scale;
      isPanning = false;
      return;
    }

    if (event.touches.length === 1) {
      var now = Date.now();
      if (now - lastTapTime < 300) {
        toggleZoom();
        lastTapTime = 0;
        return;
      }
      lastTapTime = now;

      if (scale > 1) {
        isPanning = true;
        panStartX = event.touches[0].clientX;
        panStartY = event.touches[0].clientY;
        panOriginX = translateX;
        panOriginY = translateY;
        modalImg.classList.add('is-panning');
      }
    }
  }, { passive: true });

  modalImg.addEventListener('touchmove', function (event) {
    if (event.touches.length === 2 && pinchStartDistance > 0) {
      var ratio = touchDistance(event.touches) / pinchStartDistance;
      scale = Math.max(1, Math.min(MAX_SCALE, pinchStartScale * ratio));
      clampTranslate();
      applyTransform();
      return;
    }

    if (event.touches.length === 1 && isPanning) {
      translateX = panOriginX + (event.touches[0].clientX - panStartX);
      translateY = panOriginY + (event.touches[0].clientY - panStartY);
      clampTranslate();
      applyTransform();
    }
  }, { passive: true });

  modalImg.addEventListener('touchend', function (event) {
    if (event.touches.length === 0) {
      isPanning = false;
      pinchStartDistance = 0;
      modalImg.classList.remove('is-panning');
      if (scale <= 1.02) resetTransform();
    }
  });

  // 桌面端双击也支持缩放/还原，跟移动端双击手势保持一致，降低学习成本
  modalImg.addEventListener('dblclick', toggleZoom);
})();